/**
 * Ctrl/⌘+[ goes back to where you last were in another tool — from a chat
 * opened on Home back to Home, from a finding back to the chat that raised
 * it. The rail's sections are the tools; moving within one tool (thread to
 * thread) never becomes the way back.
 *
 * @module components/toolBackHistory
 */

export interface ToolPlace<Tool extends string = string> {
  readonly tool: Tool;
  /** pathname + search + hash, as navigated. */
  readonly href: string;
}

export interface ToolBackHistory<Tool extends string = string> {
  readonly current: ToolPlace<Tool> | null;
  /** The last place in a different tool than `current`. */
  readonly previous: ToolPlace<Tool> | null;
}

export const EMPTY_TOOL_BACK_HISTORY: ToolBackHistory<never> = { current: null, previous: null };

/** Records a visit: crossing into another tool remembers where you left. */
export function recordToolPlace<Tool extends string>(
  history: ToolBackHistory<Tool>,
  place: ToolPlace<Tool>,
): ToolBackHistory<Tool> {
  const { current } = history;
  if (current === null) return { current: place, previous: history.previous };
  if (current.tool === place.tool) return { current: place, previous: history.previous };
  return { current: place, previous: current };
}

/** Where Ctrl/⌘+[ goes, or null when there is nowhere else to go back to. */
export function toolBackTarget<Tool extends string>(history: ToolBackHistory<Tool>): string | null {
  const { current, previous } = history;
  if (previous === null || (current !== null && previous.tool === current.tool)) return null;
  return previous.href;
}

/** Ctrl+[ on Windows/Linux, ⌘+[ on macOS; never with Shift (that is thread.previous) or Alt. */
export function isToolBackShortcut(
  event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">,
  isMac: boolean,
): boolean {
  if (event.key !== "[" || event.shiftKey || event.altKey) return false;
  return isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}
